/**
 * @file PlacementSheet.tsx
 * @description Шторка массового размещения партий (phase19.24 этап 1, шаг 4).
 *
 * Шторка справа, а не диалог по центру: список выбранных партий бывает
 * длинным, и кладовщик должен видеть, что именно он раскладывает, не теряя из
 * виду отбор под шторкой (то же решение, что у подбора в сборке — шаг 2).
 *
 * Два режима вместо двух экранов: «одна ячейка для всех» закрывает типовой
 * случай «приехала приёмка, всё на один стеллаж», «по строкам» — разбор по
 * местам. Режим — данные формы, разметка одна.
 *
 * Документ проводится сразу (`commit: true`): черновик, который надо потом
 * найти и провести, из журнала остатков никому не нужен. Но атомарности у
 * проведения нет: упав на строке, сервер оставляет документ черновиком —
 * шторка показывает полосу со ссылкой на него, иначе MV-документ висит в
 * журнале, а кладовщик о нём не знает (находка ревью 2026-09-20).
 *
 * Партии, уже лежащие в выбранной ячейке, сервер пропускает и перечисляет в
 * `skipped` (решение пользователя 2026-09-20). Шторка в этом случае остаётся
 * открытой с полосой `info`: закрыться молча значит оставить кладовщика в
 * уверенности, что уехало всё выбранное.
 */

import { Loader2, MapPin } from "lucide-react";

import { PLACEMENT_TEXT } from "./stock-placement-texts";
import { PlacementBinField } from "./PlacementBinField";
import { PlacementNotices } from "./PlacementNotices";
import { PlacementRows } from "./PlacementRows";
import { usePlacementForm } from "./use-placement-form";
import type { PlacementMode } from "./stock-placement-payload";
import { Button } from "../../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { cn } from "@/lib/utils";
import type { PlacementResult } from "../../../lib/api-transfers";
import type { StockLevelDto } from "../../../lib/api-wms";

interface Props {
  open: boolean;
  onClose: () => void;
  rows: StockLevelDto[];
  /** Ответ сервера: вызывающий обновляет отбор, показывает тост и закрывает
   *  шторку, если пропущенных партий не было. */
  onPlaced: (result: PlacementResult, placedCount: number) => void;
  /** Проведение упало — вызывающий перечитывает отбор остатков. */
  onFailed: () => void;
  /** Адрес карточки перемещения по id — ссылка в полосе об отказе. */
  transferHref: (transferId: string) => string;
}

/** Переключатель режима — пилюля 56px, как чипсы фильтров остатков. */
function ModeToggle({
  label,
  pressed,
  onPress,
}: {
  label: string;
  pressed: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onPress}
      className={cn(
        "min-h-14 flex-1 rounded-full px-4 text-sm transition-colors duration-200",
        pressed
          ? "bg-primary font-medium text-on-primary"
          : "bg-surface-low text-on-surface-variant hover:bg-accent",
      )}
    >
      {label}
    </button>
  );
}

export function PlacementSheet({
  open,
  onClose,
  rows,
  onPlaced,
  onFailed,
  transferHref,
}: Props) {
  const form = usePlacementForm({ open, rows, onPlaced, onFailed });
  const modes: { value: PlacementMode; label: string }[] = [
    { value: "shared", label: PLACEMENT_TEXT.modeShared },
    { value: "perRow", label: PLACEMENT_TEXT.modePerRow },
  ];

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full max-w-none gap-4 overflow-y-auto sm:max-w-xl"
      >
        <SheetHeader>
          <SheetTitle>{PLACEMENT_TEXT.sheetTitle}</SheetTitle>
          <p className="text-sm text-on-surface-variant">
            {PLACEMENT_TEXT.sheetDescription}
          </p>
        </SheetHeader>

        <div className="flex gap-2">
          {modes.map((mode) => (
            <ModeToggle
              key={mode.value}
              label={mode.label}
              pressed={form.mode === mode.value}
              onPress={() => form.setMode(mode.value)}
            />
          ))}
        </div>

        <PlacementNotices
          binsError={form.binsError}
          noBins={!form.binsLoading && !form.binsError && form.bins.length === 0}
          error={form.error}
          transferHref={transferHref}
          skippedNotice={form.skippedNotice}
        />

        {form.mode === "shared" ? (
          <PlacementBinField
            label={PLACEMENT_TEXT.sharedBinLabel}
            bins={form.bins}
            value={form.sharedBinId}
            onChange={form.setSharedBinId}
            warehouseId={form.warehouseId}
            disabled={form.binsLoading || form.saving}
          />
        ) : null}

        <PlacementRows
          rows={rows}
          mode={form.mode}
          bins={form.bins}
          warehouseId={form.warehouseId}
          binByRow={form.binByRow}
          onRowBin={form.setRowBin}
          errors={form.error?.byLot ?? {}}
          disabled={form.saving}
        />

        {/* Кнопка внизу потока шторки: `sticky` держит её над списком, пока
            он длиннее экрана, и не накрывает последнюю строку на коротком. */}
        <div className="sticky bottom-0 flex flex-col gap-1 bg-surface-lowest pt-2">
          <Button
            onClick={() => void form.submit()}
            disabled={form.saving || !form.build.payload}
            className="w-full"
          >
            {form.saving ? (
              <Loader2 className="size-5 animate-spin" />
            ) : (
              <MapPin className="size-5" />
            )}
            <span className="ml-2">
              {form.saving ? PLACEMENT_TEXT.submitting : PLACEMENT_TEXT.submit}
            </span>
          </Button>
          {form.build.hint ? (
            <span className="text-center text-xs text-warning">
              {form.build.hint}
            </span>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
